import type { CalendarConfig, DayId } from '../domain/calendar.ts'
import { DEFAULT_CALENDAR, logicalDay } from '../domain/calendar.ts'
import type { Actor, DomainEvent } from '../domain/events.ts'
import { project } from '../domain/projections/project.ts'
import type { CommandContext, CommandResult } from '../domain/commands.ts'
import { expirePurchase, stalePurchases } from '../domain/commands.ts'
import type { FamilyState } from '../domain/state.ts'
import { EMPTY_STATE } from '../domain/state.ts'
import type { Clock, EventStore, Random } from './ports.ts'

/**
 * The seam the UI talks to. It owns three things and nothing else: the current
 * projection, the logical day, and the one path by which a command becomes
 * events in the log.
 *
 * Every command goes through `dispatch`, which means there is a single place
 * where the clock and the dice enter the system. The UI never constructs an
 * event and never writes to the store.
 */
export type Unsubscribe = () => void

export class HomeSession {
  private readonly store: EventStore
  private readonly clock: Clock
  private readonly random: Random
  private current: FamilyState = EMPTY_STATE
  private log: readonly DomainEvent[] = []
  private calendarConfig: CalendarConfig = DEFAULT_CALENDAR
  private readonly listeners = new Set<() => void>()

  private constructor(store: EventStore, clock: Clock, random: Random) {
    this.store = store
    this.clock = clock
    this.random = random
  }

  static async open(store: EventStore, clock: Clock, random: Random): Promise<HomeSession> {
    const session = new HomeSession(store, clock, random)
    await session.reload()
    return session
  }

  /** Replays the whole log. Cheap enough to do after every command. */
  async reload(): Promise<void> {
    const events = await this.store.readAll()
    this.log = events
    this.current = project(events)
    const family = this.current.family
    this.calendarConfig = family
      ? { timezone: family.timezone, dayCutoffHour: family.dayCutoffHour }
      : DEFAULT_CALENDAR
    for (const listener of this.listeners) listener()
  }

  /**
   * Un identificador nuevo.
   *
   * La pantalla de alta los necesita antes de que exista la casa, y en esta app el
   * azar entra por un solo sitio: el mismo generador que va a los eventos.
   */
  newId(): string {
    return this.random.id()
  }

  state(): FamilyState {
    return this.current
  }

  /** The raw log, for readings that need the shape of the attempts and not
   *  only where they ended up. */
  events(): readonly DomainEvent[] {
    return this.log
  }

  calendar(): CalendarConfig {
    return this.calendarConfig
  }

  /** The day the house is on right now, which is not always today's date. */
  today(): DayId {
    return logicalDay(this.clock.now(), this.calendarConfig)
  }

  context(actor: Actor, day?: DayId): CommandContext {
    return {
      now: this.clock.now(),
      day: day ?? this.today(),
      actor,
      newId: () => this.random.id(),
      draw: () => this.random.float(),
    }
  }

  /**
   * Runs a command. On acceptance the events are appended and the projection is
   * rebuilt; on refusal nothing at all is written.
   */
  async dispatch(
    command: (state: FamilyState, ctx: CommandContext) => CommandResult,
    actor: Actor,
    day?: DayId,
  ): Promise<CommandResult> {
    const result = command(this.current, this.context(actor, day))
    if (!result.ok) return result
    if (result.events.length > 0) {
      await this.store.append(result.events)
      await this.reload()
    }
    return result
  }

  /**
   * Catches up on what the calendar decided while nobody was looking.
   *
   * The log is a record of things that happened, so a day going by cannot
   * change it on its own: something has to notice and write it down. This runs
   * once when the app opens and gives back the coins for screen time that was
   * bought, never granted, and belonged to a day that is now over.
   *
   * It is the system's own doing, not a parent's, and the events say so.
   */
  async reconcile(): Promise<number> {
    let done = 0
    try {
      for (const purchaseId of stalePurchases(this.current, this.today())) {
        await this.dispatch((s, c) => expirePurchase(s, { purchaseId }, c), { kind: 'system' })
        done += 1
      }
    } catch (cause) {
      // Best effort, and deliberately silent about it to the caller. This runs
      // between opening the store and the first paint; a tidy-up that fails is
      // never a reason for a child to find the app will not start. The next
      // open tries again, because the log still says the purchase is pending.
      console.error('No se pudo poner al día el registro', cause)
    }
    return done
  }

  /** Appends events that are not the product of a command, such as the seed. */
  async seed(events: readonly DomainEvent[]): Promise<void> {
    if (events.length === 0) return
    await this.store.append(events)
    await this.reload()
  }

  subscribe(listener: () => void): Unsubscribe {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
}
